import { authFetch } from "@opensea/auth"
import type { Connection } from "@opensea/wallet"
import { parseSiwxMessage } from "@opensea/wallet"
import type {
  LinkPrivyWalletProps,
  LinkWalletProps,
  LinkWalletResponse,
  UnlinkWalletProps,
  UnlinkWalletResponse,
} from "./accounts-adapter.types"
import { siweAdapter } from "./siwe-adapter"

class OSAccountsAdapter {
  async createSIWXMessage(
    walletAddress: string,
    connection: Connection,
  ): Promise<string> {
    // Get nonce from SIWE adapter
    const nonce = await siweAdapter.getNonce()

    // Ensure chain ID is a number (not string) - createSiwxMessage expects number
    const chainId = Number(connection.chainId)

    // Create SIWX message using adapter (let it use default statement)
    const message = siweAdapter.createMessage({
      nonce,
      address: walletAddress,
      chainId, // Must be number for EIP-155 validation
      // Use default statement (with terms of service) to match parseSiwxMessage expectations
    })

    return message
  }

  async linkWallet({
    connection,
    signature,
    message,
    force,
  }: LinkWalletProps): Promise<LinkWalletResponse> {
    try {
      const parsedMessage = parseSiwxMessage(message)

      const linkResponse = await authFetch("/auth/accounts/wallets/siwx", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: parsedMessage,
          signature,
          chainArch: connection.connector.chainArch,
          connectorId: connection.connector.id,
          visibility: "private", // Force private for MVP
          forceLinking: force,
        }),
      })

      if (!linkResponse.ok) {
        const errorData = await linkResponse.json().catch(() => ({}))
        const message = errorData.error?.message || "Failed to link wallet"
        // race condition or out of sync state on FE -- treat as success
        if (message.includes("Wallet already linked to current account")) {
          return { success: true }
        }
        if (message.includes("Wallet already linked")) {
          return { success: false, message, code: "ALREADY_LINKED" }
        }
        return { success: false, message }
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }

  async linkPrivyWallet({
    token,
    force,
  }: LinkPrivyWalletProps): Promise<LinkWalletResponse> {
    try {
      const linkResponse = await authFetch("/auth/accounts/wallets/privy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          visibility: "private", // Force private for MVP
          forceLinking: force,
        }),
      })

      if (!linkResponse.ok) {
        const errorData = await linkResponse.json().catch(() => ({}))
        const message = errorData.error?.message || "Failed to link wallet"
        // race condition or out of sync state on FE -- treat as success
        if (message.includes("Wallet already linked to current account")) {
          return { success: true }
        }
        if (message.includes("Wallet already linked")) {
          return { success: false, message, code: "ALREADY_LINKED" }
        }
        return { success: false, message }
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }

  async unlinkWallet({
    walletAddress,
  }: UnlinkWalletProps): Promise<UnlinkWalletResponse> {
    try {
      const response = await authFetch(
        `/auth/accounts/wallets/${walletAddress}`,
        {
          method: "DELETE",
        },
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const message = errorData.error?.message || "Failed to unlink wallet"
        // race condition or out of sync state on FE -- treat as success
        // TODO: Use proper error matching as BE returns it
        if (message.includes("is not linked to account")) {
          return { success: true }
        }

        return { success: false, message }
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Unknown error",
      }
    }
  }
}

export const accountsAdapter = new OSAccountsAdapter()
